import type { ApiClientCore } from './apiClient.core.js';
import type { ApiResponse } from './apiClient.types.js';

export interface EffectiveProviderSlot {
  readonly providerKind: string;
  readonly providerSlotId: string;
  readonly isEnabled: true;
}

export const bankAccountsMethods = {
  async getAdminBankAccountProviderPolicy(this: ApiClientCore): Promise<
    ApiResponse<{
      stripeIntegrationMode: 'DIRECT' | 'CONNECT';
      slots: EffectiveProviderSlot[];
    }>
  > {
    return this.get('/api/v1/admin/bank-accounts/provider-policy', false);
  },

  async upsertBankAccountRailCredentials(
    this: ApiClientCore,
    bankAccountId: string,
    railId: string,
    secrets: Record<string, string>
  ): Promise<ApiResponse<{ updated: boolean }>> {
    return this.put(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/credentials`,
      { secrets },
      false
    );
  },

  async deleteBankAccountRailCredential(
    this: ApiClientCore,
    bankAccountId: string,
    railId: string,
    secretKey: string
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/credentials/${encodeURIComponent(secretKey)}`,
      false
    );
  },

  async upsertBankAccountAttachmentCredentials(
    this: ApiClientCore,
    bankAccountId: string,
    attachmentId: string,
    secrets: Record<string, string>
  ): Promise<ApiResponse<{ updated: boolean }>> {
    return this.put(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments/${encodeURIComponent(attachmentId)}/credentials`,
      { secrets },
      false
    );
  },

  async deleteBankAccountAttachmentCredential(
    this: ApiClientCore,
    bankAccountId: string,
    attachmentId: string,
    secretKey: string
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments/${encodeURIComponent(attachmentId)}/credentials/${encodeURIComponent(secretKey)}`,
      false
    );
  },

  async createBankAccountAttachment(
    this: ApiClientCore,
    bankAccountId: string,
    body: { providerKind: string; providerSlotId: string; isEnabled?: boolean }
  ): Promise<
    ApiResponse<{
      attachmentId: string;
      providerKind: string;
      providerSlotId: string;
      isEnabled: boolean;
    }>
  > {
    return this.post(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments`,
      body,
      false
    );
  },

  async deleteBankAccountAttachment(
    this: ApiClientCore,
    bankAccountId: string,
    attachmentId: string
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments/${encodeURIComponent(attachmentId)}`,
      false
    );
  },

  async startBankAccountStripeConnectOnboarding(
    this: ApiClientCore,
    bankAccountId: string,
    railId: string
  ): Promise<ApiResponse<{ url: string; stripeConnectAccountId: string; expiresAt: number }>> {
    return this.post(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/stripe-connect/onboard`,
      {},
      false
    );
  },

  async getBankAccountStripeConnectOnboardingStatus(
    this: ApiClientCore,
    bankAccountId: string,
    railId: string
  ): Promise<
    ApiResponse<{
      status: {
        stripeIntegrationMode: 'DIRECT' | 'CONNECT';
        stripeConnectProductModel: 'SAAS_PLATFORM' | 'MARKETPLACE';
        stripeConnectOnboardingStatus: string;
        stripeConnectAccountId: string | null;
        chargesEnabled: boolean | null;
        payoutsEnabled: boolean | null;
        detailsSubmitted: boolean | null;
      };
    }>
  > {
    return this.get(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/stripe-connect/status`,
      false
    );
  },

  async getBankAccountProviderStatus(this: ApiClientCore, bankAccountId: string): Promise<
    ApiResponse<{
      providers: Array<{
        scope: 'rail' | 'attachment';
        scopeId: string;
        providerKind: string;
        providerSlotId: string;
        isEnabled: boolean;
        slotEnabled: boolean;
        hasEnabledAttachment: boolean;
        hasCredentials: boolean;
        isConfigured: boolean;
        credentialsPresent: boolean;
        configurationBlocker?: 'ENCRYPTION_UNAVAILABLE';
        verificationStatus?: 'pending' | 'verified' | 'failed';
        verifiedAt?: string | null;
        lastProbeAt?: string | null;
        lastProbeSummary?: string | null;
        probeCooldownUntil?: string | null;
      }>;
    }>
  > {
    return this.get(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/provider-status`,
      false
    );
  },

  async verifyBankAccountProviderWiring(
    this: ApiClientCore,
    bankAccountId: string,
    providerKind: string,
    providerSlotId: string
  ): Promise<
    ApiResponse<{
      verificationStatus: 'pending' | 'verified' | 'failed';
      summary: string;
      probeCooldownUntil?: string | null;
      waitSeconds?: number;
    }>
  > {
    return this.post(
      `/api/v1/admin/bank-accounts/${encodeURIComponent(bankAccountId)}/provider-wiring/${encodeURIComponent(providerKind)}/${encodeURIComponent(providerSlotId)}/verify`,
      {},
      false
    );
  },
};
